/**
 * Browser-back support for fullscreen overlays.
 *
 * Each overlay pushes a synthetic history entry when it opens.
 * Browser back pops it → closes the topmost overlay (LIFO order).
 * Programmatic close (X, ESC, backdrop) calls history.back() to balance the stack.
 *
 * Module-level state ensures correct LIFO ordering across all overlay instances,
 * including nested overlays (e.g., media center → photo lightbox).
 *
 * Orphan handling: when a non-topmost overlay is removed from the stack (e.g. parent
 * unmounts while child is open), its browser history entry becomes orphaned. The
 * orphanCount tracker ensures these entries are auto-skipped during subsequent back
 * navigation so the user never experiences a "dead" back press.
 */

type HistoryEntry = {
  id: string;
  close: () => void;
};

const stack: Array<HistoryEntry> = [];
const retiredEntryIds = new Set<string>();
let skipCount = 0;
let orphanCount = 0;
let listening = false;

function ensureListening(): void {
  if (!listening) {
    window.addEventListener('popstate', onPopstate);
    listening = true;
  }
}

function cleanupIfIdle(): void {
  if (stack.length === 0 && skipCount === 0 && orphanCount === 0 && listening) {
    window.removeEventListener('popstate', onPopstate);
    listening = false;
    retiredEntryIds.clear();
  }
}

/**
 * After popping a real entry or consuming a skip, if the stack is empty and there
 * are orphaned history entries below, chain another history.back() to skip past the
 * next orphan. The resulting popstate will decrement skipCount, and if more orphans
 * remain, chain again — until all orphans are consumed.
 */
function skipOrphanIfNeeded(): void {
  if (stack.length === 0 && orphanCount > 0) {
    orphanCount--;
    skipCount++;
    history.back();
  }
}

function onPopstate(event: PopStateEvent): void {
  if (skipCount > 0) {
    skipCount--;
    skipOrphanIfNeeded();
    cleanupIfIdle();
    return;
  }

  const destinationEntryId = (event.state as { haloOverlay?: unknown } | null)?.haloOverlay;
  if (typeof destinationEntryId === 'string' && retiredEntryIds.has(destinationEntryId)) {
    skipCount++;
    history.back();
    return;
  }

  const top = stack.pop();
  if (top) {
    retiredEntryIds.add(top.id);
    top.close();
  }
  skipOrphanIfNeeded();
  cleanupIfIdle();
}

/**
 * Push a history entry for an overlay.
 */
export function pushOverlayHistory(id: string, closeFn: () => void): void {
  ensureListening();
  retiredEntryIds.delete(id);
  stack.push({ id, close: closeFn });
  history.pushState({ haloOverlay: id }, '');
}

/**
 * Pop a history entry for a mobile overlay that was closed programmatically
 * (close button, ESC, backdrop). Calls history.back() to balance the stack.
 *
 * Only pops browser history when the entry is topmost — if a parent overlay
 * unmounts while a child is still open, the entry is tracked as an orphan
 * and auto-skipped during subsequent back navigation.
 */
export function popOverlayHistory(id: string): void {
  const idx = stack.findIndex((e) => e.id === id);
  if (idx === -1) return;

  if (idx === stack.length - 1) {
    // Topmost entry — safe to pop browser history
    stack.pop();
    retiredEntryIds.add(id);
    skipCount++;
    history.back();
    // Don't cleanup listener here — the pending popstate from history.back()
    // needs to fire first so onPopstate can decrement skipCount.
  } else {
    // Not topmost — remove from our stack only and track the orphaned
    // browser history entry. onPopstate will auto-skip it later.
    stack.splice(idx, 1);
    orphanCount++;
    // Don't cleanup — orphanCount > 0 keeps the listener alive.
  }
}

/**
 * Orphan an overlay's history entry without calling history.back().
 * Used when an overlay unmounts while still open (e.g., user navigates away
 * from inside a spoke). We don't want to call history.back() because the
 * browser is already handling a real navigation — an extra back would fight it.
 */
export function orphanOverlayHistory(id: string): void {
  const idx = stack.findIndex((e) => e.id === id);
  if (idx === -1) return;
  stack.splice(idx, 1);
  orphanCount++;
  // Don't cleanup — orphanCount > 0 keeps the listener alive to auto-skip later.
}

/** Reset all state. Exported for test cleanup only. */
export function resetOverlayHistory(): void {
  stack.length = 0;
  retiredEntryIds.clear();
  skipCount = 0;
  orphanCount = 0;
  if (listening) {
    window.removeEventListener('popstate', onPopstate);
    listening = false;
  }
}
